import { useState, useEffect } from 'react'
import { GitMerge, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useMergePreview, useMergeRecords } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import type { FieldSelection, DuplicateEntityType, MergeFieldComparison } from '@/types'
import { GRACKLE_COLORS, SEMANTIC } from '@/lib/theme'

interface MergeRecordsModalProps {
  entityType: Exclude<DuplicateEntityType, 'CrossEntity'>
  recordIdA: string
  recordIdB: string
  open: boolean
  onOpenChange: (open: boolean) => void
  duplicateMatchId?: string
}

type Step = 'select-master' | 'field-selection' | 'confirm'

export function MergeRecordsModal({
  entityType,
  recordIdA,
  recordIdB,
  open,
  onOpenChange,
  duplicateMatchId: _duplicateMatchId,
}: MergeRecordsModalProps) {
  const { toast } = useToast()
  const [step, setStep] = useState<Step>('select-master')
  const [masterId, setMasterId] = useState<string>('')
  const [loserId, setLoserId] = useState<string>('')
  const [fieldSelections, setFieldSelections] = useState<FieldSelection>({})

  const { data: preview, isLoading: previewLoading } = useMergePreview(
    entityType,
    recordIdA,
    recordIdB
  )
  const mergeMutation = useMergeRecords()

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep('select-master')
      setMasterId('')
      setLoserId('')
      setFieldSelections({})
    }
  }, [open, recordIdA, recordIdB])

  // Auto-populate field selections when preview loads and master is selected
  useEffect(() => {
    if (!preview || !masterId) return

    const selections: FieldSelection = {}
    for (const field of preview.fields) {
      if (field.masterValue === field.loserValue) {
        // Same value — use master
        selections[field.key] = 'master'
      } else if (field.masterValue == null && field.loserValue != null) {
        // Master is empty, loser has value — prefer loser
        const effectiveMaster = masterId === recordIdA ? 'loser' : 'master'
        const effectiveLoser = masterId === recordIdA ? 'master' : 'loser'
        selections[field.key] = masterId === recordIdA ? effectiveMaster : effectiveLoser
        // Actually, since preview.master is always recordIdA:
        selections[field.key] = masterId === recordIdA ? 'loser' : 'master'
      } else if (field.masterValue != null && field.loserValue == null) {
        selections[field.key] = masterId === recordIdA ? 'master' : 'loser'
      } else {
        // Both have different values — default to master's
        selections[field.key] = masterId === recordIdA ? 'master' : 'loser'
      }
    }
    setFieldSelections(selections)
  }, [preview, masterId, recordIdA])

  const handleSelectMaster = (id: string) => {
    setMasterId(id)
    setLoserId(id === recordIdA ? recordIdB : recordIdA)
  }

  const handleFieldSelect = (key: string, value: 'master' | 'loser') => {
    setFieldSelections((prev) => ({ ...prev, [key]: value }))
  }

  const handleConfirm = async () => {
    if (!masterId || !loserId) return

    // Remap field selections relative to the actual master/loser
    // preview.master = recordIdA, preview.loser = recordIdB always
    // If user picked recordIdA as master, selections are correct as-is
    // If user picked recordIdB as master, we need to swap
    let effectiveSelections = fieldSelections
    if (masterId === recordIdB) {
      effectiveSelections = {}
      for (const [key, val] of Object.entries(fieldSelections)) {
        effectiveSelections[key] = val === 'master' ? 'loser' : 'master'
      }
    }

    try {
      await mergeMutation.mutateAsync({
        entityType,
        masterId,
        loserId,
        fieldSelections: effectiveSelections,
      })
      toast({
        title: 'Records Merged',
        description: `Successfully merged into the master record.`,
      })
      onOpenChange(false)
    } catch {
      toast({
        title: 'Merge Failed',
        description: 'An error occurred during the merge. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const masterName = preview
    ? masterId === recordIdA
      ? String(preview.master.name || '')
      : String(preview.loser.name || '')
    : ''
  const loserName = preview
    ? loserId === recordIdA
      ? String(preview.master.name || '')
      : String(preview.loser.name || '')
    : ''

  const loserCounts =
    loserId === recordIdA ? preview?.masterRelatedCounts : preview?.loserRelatedCounts

  // For field display, preview.master = recordIdA, preview.loser = recordIdB always
  // Labels should show which the user selected as master/loser
  const getFieldMasterValue = (field: MergeFieldComparison) =>
    masterId === recordIdA ? field.masterValue : field.loserValue
  const getFieldLoserValue = (field: MergeFieldComparison) =>
    masterId === recordIdA ? field.loserValue : field.masterValue

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh]"
        aria-describedby="merge-dialog-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5" aria-hidden="true" />
            Merge {entityType}s{step === 'select-master' && ' — Step 1: Select Master'}
            {step === 'field-selection' && ' — Step 2: Choose Values'}
            {step === 'confirm' && ' — Step 3: Confirm'}
          </DialogTitle>
          <DialogDescription id="merge-dialog-description">
            {step === 'select-master' &&
              'Choose which record to keep as the master. The other record will be merged into it.'}
            {step === 'field-selection' &&
              'For each field, choose which value to keep in the master record.'}
            {step === 'confirm' &&
              'Review your selections and confirm the merge. This action cannot be undone.'}
          </DialogDescription>
        </DialogHeader>

        {previewLoading ? (
          <div className="flex items-center justify-center py-12" role="status">
            <Loader2
              className="h-8 w-8 animate-spin"
              style={{ color: GRACKLE_COLORS.navy, opacity: 0.4 }}
              aria-hidden="true"
            />
            <span className="sr-only">Loading records...</span>
          </div>
        ) : !preview ? (
          <div
            className="text-center py-8"
            style={{ color: GRACKLE_COLORS.darkRaspberry }}
            role="alert"
          >
            Failed to load records for comparison.
          </div>
        ) : (
          <>
            {/* Step 1: Select Master */}
            {step === 'select-master' && (
              <div className="space-y-4">
                <div
                  className="grid grid-cols-2 gap-4"
                  role="radiogroup"
                  aria-label="Select master record"
                >
                  {[
                    { id: recordIdA, data: preview.master },
                    { id: recordIdB, data: preview.loser },
                  ].map(({ id, data }) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={masterId === id}
                      className="p-4 border-2 rounded-lg text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      style={{
                        borderColor:
                          masterId === id ? GRACKLE_COLORS.navy : GRACKLE_COLORS.powderBlue,
                        backgroundColor:
                          masterId === id ? `${SEMANTIC.info.fill}1A` : 'transparent',
                      }}
                      onClick={() => handleSelectMaster(id)}
                    >
                      <p className="font-semibold text-lg truncate">{String(data.name || id)}</p>
                      {data.email ? (
                        <p className="text-sm text-muted-foreground truncate">
                          {String(data.email)}
                        </p>
                      ) : null}
                      {data.phone ? (
                        <p className="text-sm text-muted-foreground truncate">
                          {String(data.phone)}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground mt-2">ID: {id}</p>
                      {masterId === id && (
                        <Badge
                          className="mt-2"
                          style={{
                            backgroundColor: SEMANTIC.info.fill,
                            color: SEMANTIC.info.text,
                          }}
                        >
                          Master
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button onClick={() => setStep('field-selection')} disabled={!masterId}>
                    Next: Choose Values
                    <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Field-by-Field Selection */}
            {step === 'field-selection' && (
              <div className="space-y-4">
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-2">
                    {preview.fields.map((field) => {
                      const masterVal = getFieldMasterValue(field)
                      const loserVal = getFieldLoserValue(field)
                      const same = masterVal === loserVal
                      const selection = fieldSelections[field.key]

                      return (
                        <fieldset
                          key={field.key}
                          className="p-3 border rounded-lg"
                          style={{
                            backgroundColor: same ? GRACKLE_COLORS.paleGrey : 'transparent',
                          }}
                          aria-label={`Select value for ${field.label}`}
                        >
                          <legend className="text-xs font-semibold text-muted-foreground px-1">
                            {field.label}
                          </legend>
                          {same ? (
                            <p className="text-sm py-1">
                              {masterVal != null ? (
                                String(masterVal)
                              ) : (
                                <span className="text-muted-foreground italic">Empty</span>
                              )}
                              <span className="text-xs text-muted-foreground ml-2">(Same)</span>
                            </p>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 mt-1">
                              <label
                                className="flex items-start gap-2 p-2 border rounded cursor-pointer transition-colors hover:bg-muted"
                                style={{
                                  borderColor:
                                    selection === 'master' ? GRACKLE_COLORS.navy : undefined,
                                  backgroundColor:
                                    selection === 'master' ? `${SEMANTIC.info.fill}1A` : undefined,
                                }}
                              >
                                <input
                                  type="radio"
                                  name={field.key}
                                  checked={selection === 'master'}
                                  onChange={() => handleFieldSelect(field.key, 'master')}
                                  className="mt-1 accent-primary"
                                  aria-label={`Use ${masterVal != null ? String(masterVal) : 'empty'} from master record for ${field.label}`}
                                />
                                <div className="min-w-0">
                                  <p className="text-xs text-muted-foreground">
                                    Master ({masterName})
                                  </p>
                                  <p className="text-sm truncate">
                                    {masterVal != null ? (
                                      String(masterVal)
                                    ) : (
                                      <span className="italic text-muted-foreground">Empty</span>
                                    )}
                                  </p>
                                </div>
                              </label>
                              <label
                                className="flex items-start gap-2 p-2 border rounded cursor-pointer transition-colors hover:bg-muted"
                                style={{
                                  borderColor:
                                    selection === 'loser' ? GRACKLE_COLORS.navy : undefined,
                                  backgroundColor:
                                    selection === 'loser' ? `${SEMANTIC.info.fill}1A` : undefined,
                                }}
                              >
                                <input
                                  type="radio"
                                  name={field.key}
                                  checked={selection === 'loser'}
                                  onChange={() => handleFieldSelect(field.key, 'loser')}
                                  className="mt-1 accent-primary"
                                  aria-label={`Use ${loserVal != null ? String(loserVal) : 'empty'} from duplicate record for ${field.label}`}
                                />
                                <div className="min-w-0">
                                  <p className="text-xs text-muted-foreground">
                                    Duplicate ({loserName})
                                  </p>
                                  <p className="text-sm truncate">
                                    {loserVal != null ? (
                                      String(loserVal)
                                    ) : (
                                      <span className="italic text-muted-foreground">Empty</span>
                                    )}
                                  </p>
                                </div>
                              </label>
                            </div>
                          )}
                        </fieldset>
                      )
                    })}
                  </div>
                </ScrollArea>

                <div className="flex justify-between gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setStep('select-master')}>
                    Back
                  </Button>
                  <Button onClick={() => setStep('confirm')}>
                    Next: Review
                    <ArrowRight className="h-4 w-4 ml-1" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 'confirm' && (
              <div className="space-y-4">
                <Card
                  style={{
                    borderColor: SEMANTIC.warning.fill,
                    backgroundColor: SEMANTIC.warning.fill,
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle
                        className="h-5 w-5 flex-shrink-0 mt-0.5"
                        style={{ color: GRACKLE_COLORS.prussianBlue }}
                        aria-hidden="true"
                      />
                      <div>
                        <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                          This action cannot be undone
                        </p>
                        <p className="text-sm mt-1" style={{ color: SEMANTIC.warning.text }}>
                          The duplicate record <strong>{loserName}</strong> will be soft-deleted and
                          all related records will be reassigned to <strong>{masterName}</strong>.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Related records summary */}
                {loserCounts && Object.values(loserCounts).some((v) => v > 0) && (
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-2">Related records to be reassigned:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(loserCounts).map(([key, count]) => {
                        if (count === 0) return null
                        return (
                          <Badge key={key} variant="outline">
                            {count} {key}
                          </Badge>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Summary of field changes */}
                <div className="p-4 border rounded-lg">
                  <p className="text-sm font-medium mb-2">Fields using duplicate's value:</p>
                  {(() => {
                    const loserFields = Object.entries(fieldSelections)
                      .filter(([, val]) => val === 'loser')
                      .map(([key]) => preview?.fields.find((f) => f.key === key)?.label || key)

                    if (loserFields.length === 0) {
                      return (
                        <p className="text-sm text-muted-foreground">
                          All fields keep the master's values.
                        </p>
                      )
                    }
                    return (
                      <div className="flex flex-wrap gap-1">
                        {loserFields.map((label) => (
                          <Badge key={label} variant="secondary" className="text-xs">
                            {label}
                          </Badge>
                        ))}
                      </div>
                    )
                  })()}
                </div>

                <div className="flex justify-between gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setStep('field-selection')}>
                    Back
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={mergeMutation.isPending}
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: SEMANTIC.error.fill,
                      color: SEMANTIC.error.text,
                    }}
                  >
                    {mergeMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" />
                        Merging...
                      </>
                    ) : (
                      <>
                        <GitMerge className="h-4 w-4 mr-1" aria-hidden="true" />
                        Confirm Merge
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
